import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
} from '@tanstack/react-query'
import { useToastStore } from '../store/toast'

export function useMutationToast<
  TData = unknown,
  TError extends Error = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> & {
    successMessage?: string
    /** A fixed toast, or one chosen from the error and the mutation's input. */
    errorMessage?: string | ((error: TError, variables: TVariables) => string)
  },
): UseMutationResult<TData, TError, TVariables, TContext> {
  const addToast = useToastStore((s) => s.addToast)
  const { successMessage, errorMessage, onSuccess, onError, ...rest } = options

  return useMutation<TData, TError, TVariables, TContext>({
    ...rest,
    onSuccess: (data, variables, onMutateResult, context) => {
      if (successMessage) addToast(successMessage, 'success')
      onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      const message =
        typeof errorMessage === 'function'
          ? errorMessage(error, variables)
          : errorMessage
      if (message) addToast(message, 'error')
      onError?.(error, variables, onMutateResult, context)
    },
  })
}
